import { useEffect, useId } from 'react';

import { useRequiredContext } from '~/utils/useRequiredContext';

import { HelperTextContext } from './HelperTextProvider';

type OnOpen = (content: React.ReactNode) => (iconEvent: React.MouseEvent<HTMLElement>) => void;

export const useHelperText = () => {
  const { currentHelpers, setCurrentHelpers } = useRequiredContext(HelperTextContext);
  const id = useId();

  // Close helper by setting open to false for the given id
  const closeHelper = (helperId: string) => () => {
    setCurrentHelpers((prev) =>
      prev.map((helper) => (helper.id === helperId ? { ...helper, open: false } : helper))
    );
  };

  // Open helper: if exists, set open to true; else add new
  const openHelper: OnOpen = (content) => (elementEvent) => {
    const mainClientRect = document.getElementsByTagName('main')[0]?.getBoundingClientRect();

    const elementRect = elementEvent.currentTarget.getBoundingClientRect();
    setCurrentHelpers((prev) => {
      const exists = prev.some((helper) => helper.id === id);
      if (exists) {
        return prev.map((helper) =>
          helper.id === id ? { ...helper, open: true, content } : helper
        );
      }
      return [
        ...prev,
        {
          id,
          content,
          open: true,
          offsetY: elementRect.top - (mainClientRect?.top ?? 0),
        },
      ];
    });
  };

  // Close all helpers when pathname changes
  useEffect(() => {
    setCurrentHelpers((prev) => prev.map((helper) => ({ ...helper, open: false })));
  }, [location.pathname, setCurrentHelpers]);

  return {
    id,
    currentHelpers,
    openHelper,
    closeHelper,
  };
};
